import dayjs from 'dayjs';
import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Bell, BellOff } from 'lucide-react';

import { TextWithNAFallback } from '@/components/atoms';
import { PreviousReportsButton } from '../components/PreviousReportsButton';
import { ReportWrapper } from '../components/ReportWrapper';
import type { PreviousReportsLinks } from '../components/PreviousReportsButton/PreviousReportsButton';

type RiskAnalysisSectionProps = {
  summary: string | undefined | null;
  ongoingMonitoringSummary: string | undefined | null;
  homepageScreenshotUrl?: string | null;
  gatedContentScreenshotBase64?: string | null;
  createdAt: Date;
  previousReportsLinks?: PreviousReportsLinks | null;
  isAlert?: boolean;
  isMonitoringReport?: boolean;
};

export const RiskAnalysisSection = ({
  summary,
  ongoingMonitoringSummary,
  homepageScreenshotUrl,
  gatedContentScreenshotBase64,
  createdAt,
  previousReportsLinks,
  isAlert = false,
  isMonitoringReport = false,
}: RiskAnalysisSectionProps) => {
  const screenshotSrc = gatedContentScreenshotBase64
    ? `data:image/png;base64,${gatedContentScreenshotBase64}`
    : homepageScreenshotUrl;

  return (
    <>
      {ongoingMonitoringSummary && (
        <ReportWrapper
          title={
            <div className="flex items-center gap-3">
              <span>{`Ongoing Monitoring Summary - ${dayjs(createdAt).format('MMM, YYYY')}`}</span>
              {isMonitoringReport && (
                <div className="px-2.5 py-1 bg-gray-50 rounded-md text-sm font-medium flex items-center gap-2">
                  {isAlert ? (
                    <>
                      <div className="relative">
                        <Bell className="h-4 w-4 text-gray-900" />
                        <div className="absolute right-[1.5px] top-[1.5px] h-[6px] w-[6px] rounded-full border border-white bg-red-500" />
                      </div>
                      <span className="text-sm text-gray-900">Changes Detected</span>
                    </>
                  ) : (
                    <>
                      <BellOff className="h-4 w-4 text-neutral-900" />
                      <span className="text-sm text-neutral-800">No Changes</span>
                    </>
                  )}
                </div>
              )}
            </div>
          }
          headerAction={
            previousReportsLinks ? (
              <PreviousReportsButton previousReportsLinks={previousReportsLinks} />
            ) : undefined
          }
          showTooltip={false}
        >
          <ReactMarkdown components={markdownComponents}>{ongoingMonitoringSummary}</ReactMarkdown>
        </ReportWrapper>
      )}
      <ReportWrapper title="Risk Analysis" showTooltip={false}>
        <TextWithNAFallback
          as="div"
          className="prose prose-sm max-w-none dark:prose-invert text-gray-700 dark:text-gray-300"
        >
          {summary && (
            <div className="space-y-3 leading-relaxed">
              {screenshotSrc && (
                <div className="float-right ml-6 mb-4 w-1/3">
                  <div className="block rounded-2xl overflow-hidden border border-gray-200 shadow-lg">
                    <div className="relative aspect-[4/3] bg-gray-100">
                      <img
                        src={screenshotSrc}
                        alt={
                          gatedContentScreenshotBase64
                            ? 'Gated Content Screenshot'
                            : 'Homepage Screenshot'
                        }
                        className="w-full h-full object-cover object-top"
                        onError={e => {
                          const target = e.target as HTMLImageElement;
                          const container = target.parentElement?.parentElement?.parentElement;

                          if (container) {
                            container.style.display = 'none';
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {summary}
              </ReactMarkdown>
              {screenshotSrc && <div className="clear-both" />}
            </div>
          )}
        </TextWithNAFallback>
      </ReportWrapper>
    </>
  );
};

const markdownComponents: Components = {
  p: ({ node, ...props }) => (
    <p className="my-3 text-sm text-justify text-slate-800 leading-6" {...props} />
  ),
  h2: ({ node, ...props }) => (
    <h2 className="font-semibold mt-2 mb-1 text-slate-800 text-lg" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="font-semibold mt-2 mb-1 text-slate-800 text-base" {...props} />
  ),
  ul: ({ node, ...props }) => <ul className="list-disc pl-6 my-3 space-y-2" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-6 my-3 space-y-2" {...props} />,
  li: ({ node, ...props }) => <li className="pl-1.5 text-sm leading-6 max-w-[90ch]" {...props} />,
  a: ({ node, ...props }) => (
    <a
      className="text-[0.7rem] py-0 px-2 bg-indigo-50 text-neutral-400 rounded-full hover:underline inline-block mx-1"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  ),
  strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
  blockquote: ({ node, ...props }) => (
    <blockquote
      className="pl-4 border-l-2 my-3 italic"
      style={{ borderColor: '#EFF2FF', color: '#14203D' }}
      {...props}
    />
  ),
  hr: ({ node, ...props }) => (
    <div className="my-5 w-full">
      <hr
        className="relative -mx-6"
        style={{ borderColor: '#EFF2FF', width: 'calc(100% + 3rem)' }}
        {...props}
      />
      <div
        className="h-4 relative -mx-6"
        style={{
          width: 'calc(100% + 3rem)',
          background: 'linear-gradient(0deg, #FFFFFF 0%, rgba(244, 246, 253, 0.4) 100%)',
        }}
      />
    </div>
  ),
  table: ({ node, ...props }) => (
    <table className="my-4 w-full border-collapse text-sm" {...props} />
  ),
  thead: ({ node, ...props }) => <thead className="bg-slate-50" {...props} />,
  tbody: ({ node, ...props }) => <tbody {...props} />,
  tr: ({ node, ...props }) => <tr className="border-b last:border-b-0" {...props} />,
  th: ({ node, ...props }) => (
    <th className="border border-slate-200 px-3 py-2 text-left font-semibold" {...props} />
  ),
  td: ({ node, ...props }) => (
    <td className="border border-slate-200 px-3 py-2 align-top" {...props} />
  ),
};
